import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Award, Shield, Star, TrendingUp, RefreshCw, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import CarrierPacketBadge from '@/components/CarrierPacketBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_BADGE = {
  elite: { Icon: Trophy, ring: 'border-amber-500', glow: 'shadow-[0_0_30px_rgba(245,158,11,0.35)]' },
  top10: { Icon: Award, ring: 'border-purple-500', glow: 'shadow-[0_0_30px_rgba(168,85,247,0.3)]' },
  verified: { Icon: Shield, ring: 'border-green-500', glow: '' },
  standard: { Icon: Star, ring: 'border-border', glow: '' },
  new: { Icon: Star, ring: 'border-border', glow: '' },
};

const DriverTrustPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [packetMap, setPacketMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);

  const load = async () => {
    try {
      const [trust, compliance] = await Promise.all([
        axios.get(`${API}/driver-trust/leaderboard?limit=50&min_loads=1`),
        axios.get(`${API}/carrier-compliance/list`).catch(() => ({ data: { carriers: [] } })),
      ]);
      setDrivers(trust.data.drivers || []);
      const map = {};
      (compliance.data.carriers || []).forEach((c) => {
        if (c.email) map[c.email] = { status: c.status, missing: c.missing };
      });
      setPacketMap(map);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const recompute = async () => {
    setRecomputing(true);
    try {
      const r = await axios.post(`${API}/driver-trust/recompute-all`);
      toast.success(`Recomputed scores for ${r.data.updated} drivers`);
      await load();
    } catch (e) {
      toast.error('Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) return <Layout><div className="p-8 text-center text-muted-foreground">Loading trust scores…</div></Layout>;

  const counts = drivers.reduce((acc, d) => { acc[d.tier] = (acc[d.tier] || 0) + 1; return acc; }, {});

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="driver-trust-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold flex items-center gap-3">
              <Shield className="w-8 h-8 text-green-400" />
              Driver Trust Scores
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Composite reputation index · Brokers filter for top-tier carriers
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={recompute} disabled={recomputing} data-testid="recompute-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${recomputing ? 'animate-spin' : ''}`} />
            Recompute all
          </Button>
        </div>

        {/* TIER COUNTS */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            ['elite',    'Elite · Top 1%',  'text-amber-400'],
            ['top10',    'Top 10%',         'text-purple-400'],
            ['verified', 'Verified',        'text-green-400'],
            ['standard', 'Standard',        'text-muted-foreground'],
            ['new',      'New Carrier',     'text-blue-400'],
          ].map(([key, label, color]) => (
            <Card key={key} data-testid={`tier-count-${key}`}>
              <CardContent className="p-4">
                <div className={`text-xs uppercase tracking-wider mb-1 ${color}`}>{label}</div>
                <div className="text-2xl font-bold">{counts[key] || 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* LEADERBOARD */}
        <Card data-testid="trust-leaderboard">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Leaderboard</CardTitle>
          </CardHeader>
          <CardContent>
            {drivers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                No drivers with completed loads yet.<br />
                Once autopilot starts awarding loads, scores will populate automatically.
              </div>
            ) : (
              <div className="space-y-2">
                {drivers.map((d, i) => {
                  const t = TIER_BADGE[d.tier] || TIER_BADGE.standard;
                  const Icon = t.Icon;
                  return (
                    <div
                      key={d.driver_email}
                      data-testid={`driver-row-${i}`}
                      className={`flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors border-l-4 ${t.ring} ${t.glow}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </div>
                        <Icon className="w-5 h-5" style={{ color: d.tier_color }} />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{d.name || d.driver_email}</div>
                          <div className="text-xs text-muted-foreground truncate">{d.driver_email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <CarrierPacketBadge
                          status={packetMap[d.driver_email]?.status || 'unknown'}
                          missing={packetMap[d.driver_email]?.missing || []}
                          size="sm"
                        />
                        <Badge variant="outline" className="text-[10px]" style={{ borderColor: d.tier_color, color: d.tier_color }}>
                          {d.tier_label}
                        </Badge>
                        <div className="text-right">
                          <div className="text-2xl font-bold" style={{ color: d.tier_color }}>{d.score.toFixed(0)}</div>
                          <div className="text-[10px] text-muted-foreground">{d.loads} loads · ${Math.round(d.earnings).toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DriverTrustPage;
